import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import type { User, Session } from '@supabase/supabase-js'
import { supabase } from '@/lib/supabase'

export type UserMetadata = {
  full_name?: string
  organization?: string
  role?: string
  country?: string
  signal_contact?: string
}

type AuthState = {
  user: User | null
  session: Session | null
  loading: boolean
  error: string | null
}

type AuthContextValue = AuthState & {
  signIn: (email: string, password: string) => Promise<{ user: User | null; session: Session | null }>
  signOut: () => Promise<void>
  updateProfile: (metadata: Partial<UserMetadata>) => Promise<{ user: User }>
  getUserMetadata: () => UserMetadata
  resetPassword: (email: string) => Promise<void>
  updatePassword: (newPassword: string) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    error: null,
  })

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting session:', error)
      }
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
      }))
    })

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setState((prev) => ({
        ...prev,
        session,
        user: session?.user ?? null,
        loading: false,
      }))
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    // Don't set loading: true here - it would unmount LoginForm and lose error state
    // LoginForm has its own 'submitting' state for the button spinner
    setState((prev) => ({ ...prev, error: null }))

    const { data, error } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    })

    if (error) {
      setState((prev) => ({ ...prev, error: error.message }))
      throw error
    }

    setState((prev) => ({
      ...prev,
      user: data.user,
      session: data.session,
    }))

    return data
  }, [])

  const signOut = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }))

    const { error } = await supabase.auth.signOut()

    if (error) {
      setState((prev) => ({ ...prev, loading: false, error: error.message }))
      throw error
    }

    setState({
      user: null,
      session: null,
      loading: false,
      error: null,
    })
  }, [])

  const updateProfile = useCallback(async (metadata: Partial<UserMetadata>) => {
    const { data, error } = await supabase.auth.updateUser({
      data: metadata,
    })

    if (error) {
      throw error
    }

    setState((prev) => ({
      ...prev,
      user: data.user,
    }))

    return data
  }, [])

  const getUserMetadata = useCallback((): UserMetadata => {
    return (state.user?.user_metadata as UserMetadata) || {}
  }, [state.user])

  const resetPassword = useCallback(async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    })
    if (error) throw error
  }, [])

  const updatePassword = useCallback(async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) throw error
    // Sign out globally to invalidate all sessions (including other tabs with the reset link)
    await supabase.auth.signOut({ scope: 'global' })
  }, [])

  const value: AuthContextValue = {
    user: state.user,
    session: state.session,
    loading: state.loading,
    error: state.error,
    signIn,
    signOut,
    updateProfile,
    getUserMetadata,
    resetPassword,
    updatePassword,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
